import type { FieldExplainerContent } from './types';

export type CompoundInterestExplainerContext = {
  input: {
    principal: number;
    monthlyContribution: number;
    interestRate: number;
    years: number;
    compoundFrequency: number;
    compoundFrequencyLabel: string;
  };
  result: {
    finalBalance: number;
    totalContributions: number;
    totalInterest: number;
    ruleOf72: number | null;
    firstYearInterest: number;
  };
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const usdCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

export function getCompoundInterestFieldExplainers(ctx: CompoundInterestExplainerContext): FieldExplainerContent[] {
  const { principal, monthlyContribution, interestRate, years, compoundFrequency, compoundFrequencyLabel } = ctx.input;
  const { finalBalance, totalContributions, totalInterest, ruleOf72, firstYearInterest } = ctx.result;

  const n = compoundFrequency;
  const annualRate = interestRate / 100;
  const periodRate = annualRate / n;
  const totalPeriods = n * years;
  const annualContribution = monthlyContribution * 12;
  const periodContribution = annualContribution / n;
  const effectiveAnnual = Math.pow(1 + periodRate, n) - 1;
  const principalAlone = principal * Math.pow(1 + periodRate, totalPeriods);
  const contributionShare = totalContributions > 0 ? (totalInterest / totalContributions) * 100 : 0;
  const frequencyWord = compoundFrequencyLabel.toLowerCase();
  const periodNoun = n === 1 ? 'year' : n === 2 ? 'half-year' : n === 4 ? 'quarter' : n === 12 ? 'month' : 'day';

  return [
    {
      id: 'principal',
      kind: 'input',
      label: 'Starting principal',
      plain:
        'The lump sum already in the account on day one. It is the only money in this projection that gets the full timeline to compound, which is why it carries more weight than its size suggests.',
      formula: `${usd.format(principal)} × (1 + ${interestRate}%/${n})^${totalPeriods}\n  = ${usd.format(principalAlone)} on its own\n\nthe rest of the ${usd.format(finalBalance)} comes\nfrom your monthly deposits`,
      feeds: 'The opening row of the projection table, and it is counted inside "Total contributions".'
    },
    {
      id: 'monthlyContribution',
      kind: 'input',
      label: 'Monthly contribution',
      plain: `What you add every month. The engine works in whole years, so it takes twelve of these and spreads the total evenly across the ${frequencyWord} compounding periods, depositing at the end of each one.`,
      formula:
        n === 12
          ? `${usdCents.format(monthlyContribution)} × 12 = ${usd.format(annualContribution)} a year\ndeposited as ${usdCents.format(periodContribution)} at the end\nof each of the ${n} periods`
          : `${usdCents.format(monthlyContribution)} × 12 = ${usd.format(annualContribution)} a year\nsplit ${n} ways = ${usdCents.format(periodContribution)} per ${periodNoun}\ndeposited at the end of each ${periodNoun}`,
      feeds: 'The Contributions column of the table, the contributions line on the chart, and the "Total contributions" card.',
      assumption:
        'The same amount every single month for the whole run, never missed and never raised. Nothing here steps deposits up with your income or with inflation.'
    },
    {
      id: 'interestRate',
      kind: 'input',
      label: 'Annual rate',
      plain:
        n > 1 && interestRate > 0
          ? `A nominal annual rate, not the rate you actually realise. It is chopped into ${n} slices of ${interestRate}%/${n} and applied once per period, so compounding pushes the realised annual figure above what you typed.`
          : n === 1
            ? 'A nominal annual rate applied once per year. With a single compounding period there is no intra-year compounding, so the realised annual figure equals exactly what you typed.'
            : `A nominal annual rate, chopped into ${n} slices and applied once per period. At 0% there is nothing to compound, so the realised annual figure equals what you typed.`,
      formula: `period rate = ${interestRate}% / ${n} = ${(periodRate * 100).toFixed(6)}%\napplied ${n}× a year\n\neffective annual rate\n  = (1 + ${periodRate.toFixed(8)})^${n} − 1\n  = ${(effectiveAnnual * 100).toFixed(4)}%`,
      feeds: 'Every balance on the page, the Rule of 72 estimate, and the split between contributions and interest.',
      assumption:
        'A flat rate, every period, for the whole term — no losing years and no sequence risk. Real market returns arrive unevenly, and the order they arrive in matters more the closer you get to spending the money.'
    },
    {
      id: 'years',
      kind: 'input',
      label: 'Years to grow',
      plain:
        'How long the projection runs. It sets the number of rows in the table and, multiplied by the compounding frequency, the number of times interest is applied.',
      formula: `${years} rows in the table\n${years} × ${n} = ${totalPeriods} compounding steps\n\ninterest in year 1:  ${usd.format(firstYearInterest)}\ninterest across all ${years} years: ${usd.format(totalInterest)}`,
      feeds: 'The x-axis of the chart, the length of the table, and both the projected value and total interest.'
    },
    {
      id: 'compoundFrequency',
      kind: 'input',
      label: 'Compounding frequency',
      plain: `How many times a year interest is credited. It does two jobs at once here: it divides the annual rate into ${n} smaller period rates, and it decides how finely your yearly deposits are sliced.`,
      formula: `${compoundFrequencyLabel} = ${n} period${n === 1 ? '' : 's'} a year\n\neach period:\n  balance × (1 + ${(periodRate * 100).toFixed(6)}%)\n  then + ${usdCents.format(periodContribution)}`,
      feeds:
        'The whole projection. How much frequency moves the final balance depends on the rate and the horizon: a few percent at typical rates, but a much wider gap at high rates over long runs.',
      assumption:
        'More frequent compounding always helps, but the effect flattens fast — the gap between monthly and daily is far smaller than the gap between annual and monthly, and both are dwarfed by the rate itself.'
    },
    {
      id: 'finalBalance',
      kind: 'result',
      label: 'Projected value',
      plain: `The balance at the end of year ${years} — the last cell of the table and the end of the balance line on the chart. It is what you put in plus everything the interest added on top.`,
      formula: `${usd.format(totalContributions)}  contributed\n${usd.format(totalInterest)}  interest\n  = ${usd.format(finalBalance)}`,
      feeds: 'The chart annotation and the total interest figure, which is just this number less what you paid in.',
      assumption:
        'A pre-tax, nominal figure. It has not been adjusted for inflation, so it will not buy today what the number suggests, and no tax has been taken on the interest along the way.'
    },
    {
      id: 'totalContributions',
      kind: 'result',
      label: 'Total contributions',
      plain:
        'Every dollar of your own money in the projection: the starting principal plus every monthly deposit. No growth is included, so this is simply what the plan costs you.',
      formula: `${usd.format(principal)} principal\n+ ${usdCents.format(monthlyContribution)} × 12 × ${years} years\n= ${usd.format(totalContributions)}`,
      feeds: 'The denominator of the interest-to-contributions ratio shown under "Total interest".'
    },
    {
      id: 'totalInterest',
      kind: 'result',
      label: 'Total interest',
      plain:
        totalInterest > totalContributions
          ? 'Everything the account earned beyond what you paid in. Here it has passed your own contributions, which is the point at which the account is doing more of the work than you are.'
          : 'Everything the account earned beyond what you paid in. It is measured against your contributions to show how much of the final balance you had to fund yourself.',
      formula:
        totalContributions > 0
          ? `${usd.format(finalBalance)} − ${usd.format(totalContributions)}\n  = ${usd.format(totalInterest)}\n\n${usd.format(totalInterest)} / ${usd.format(totalContributions)} = ${contributionShare.toFixed(1)}%`
          : `${usd.format(finalBalance)} − ${usd.format(totalContributions)}\n  = ${usd.format(totalInterest)}\n\nwith no money paid in, the\ninterest-to-contributions ratio is N/A`,
      feeds: 'The interest line on the chart and the percentage shown on this card.'
    },
    {
      id: 'ruleOf72',
      kind: 'result',
      label: 'Rule of 72',
      plain:
        ruleOf72 === null
          ? 'A shorthand for how long money takes to double at a given rate. It needs a rate above zero to mean anything, so it reads N/A here.'
          : 'A back-of-envelope estimate of how long a balance takes to double at this rate. It is a rule of thumb printed alongside the projection, not an output of it.',
      formula:
        ruleOf72 === null
          ? `rate is ${interestRate}%, so 72 / ${interestRate} is undefined\n  → N/A`
          : `72 / ${interestRate} = ${ruleOf72.toFixed(1)} years\n\nthe exact answer at ${(effectiveAnnual * 100).toFixed(4)}% effective is\nln(2) / ln(1 + ${effectiveAnnual.toFixed(6)}) = ${(Math.log(2) / Math.log(1 + effectiveAnnual)).toFixed(1)} years`,
      feeds: 'Nothing. It is a reference figure, and no balance on this page is derived from it.',
      assumption:
        'The rule describes a lump sum left alone. It says nothing about your monthly deposits, and it drifts from the exact answer as the rate gets further from about 8%.'
    }
  ];
}
